import {
	FaHashtag,
	FaFileAlt,
	FaLink,
	FaCalendarAlt,
	Header,
	AppEnum,
	FaFolder,
	DocumentEnums,
} from "@/Shared";

import { DocumentViewModel } from ".";

/**
 * =====================================================
 * Class: DocumentHeader
 * =====================================================
 *
 * Description:
 * Centralizes the configuration of table headers for
 * displaying DocumentViewModel entities in a UI table.
 *
 * Responsibilities:
 * - Defines icons, labels, sortability, filters, and visibility
 *   for each column
 * - Provides static `build` method to generate a complete
 *   header record based on the current enumerations
 *
 * Author: Samano CASTRE
 * Date: February 1, 2026
 */
export class DocumentHeader {
	private constructor() {}

	get id() {
		return {
			key: "id",
			icon: <FaHashtag />,
			label: "ID",
			type: "number",
			isSortable: true,
		};
	}

	get documentTitle() {
		return {
			key: "documentTitle",
			icon: <FaFileAlt />,
			label: "Titre du fichier",
			type: "string",
			isSortable: true,
			filter: {
				label: "Titre du fichier",
				case: "insensitive",
			},
		};
	}

	getDocumentType(DocumentTypeEnum : AppEnum[]) {
		return {
			key: "documentType",
			icon: <FaFolder />,
			label: "Type de document",
			type: "string",
			isSortable: true,
			filter: {
				label: "Type de document",
				case: "insensitive",
				options: DocumentTypeEnum,
			},
		};
	}

	getDocumentCategory(DocumentCategoryEnum : AppEnum[]) {
		return {
			key: "documentCategory",
			icon: <FaFolder />,
			label: "Catégori du document",
			type: "string",
			isSortable: true,
		    isHidden : true,
			filter: {
				label: "Catégorie de document",
				case: "insensitive",
				options: DocumentCategoryEnum,
			},
		};
	}

	get documentSource() {
		return {
			key: "documentSource",
			icon: <FaLink />,
			label: "URL source",
			type: "string",
			isSortable: false,
		};
	}

	get creationDate() {
		return {
			key: "creationDate",
			icon: <FaCalendarAlt />,
			label: "Date de création",
			type: "date",
			isSortable: true,
		};
	}

	/**
     * Builds a complete set of headers for the document table
     *
     * @param viewEnums - DocumentEnums containing current enums
     * @returns Record mapping DocumentViewModel keys to Header config
     */
	static build(
		viewEnums: DocumentEnums
	): Record<keyof DocumentViewModel, Header<DocumentViewModel>> {
		const header = new DocumentHeader();

		return {
			id: header.id,
			documentTitle: header.documentTitle,
			documentCategory: header.getDocumentCategory(viewEnums.DocumentCategoryEnum),
			documentType: header.getDocumentType(viewEnums.DocumentTypeEnum),
			documentSource: header.documentSource,
			creationDate: header.creationDate,
		} as Record<keyof DocumentViewModel, Header<DocumentViewModel>>;
	}
}
